import { useAuth } from '../context/AuthContext';
import apiService from '../services/ApiService';
import { apiRoutes } from '../shared/apiRoutes';
import { applyTheme, rememberedTheme, rememberTheme } from '../theme/applyTheme';

const useHashRouter = process.env.REACT_APP_USE_HASH_ROUTER === 'true';
const loginUrl = useHashRouter ? '/#/login' : '/login';

// Helper function to clear all cookies for the current domain
const clearAllCookies = () => {
  try {
    // Get all cookies
    const cookies = document.cookie.split(';');
    
    // Clear each cookie by setting it to expire in the past
    cookies.forEach(cookie => {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      
      // Clear cookie for current domain
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      
      // Also try clearing for parent domain (if applicable)
      const domain = window.location.hostname;
      const domainParts = domain.split('.');
      if (domainParts.length > 1) {
        // Try parent domain (e.g., .example.com)
        const parentDomain = '.' + domainParts.slice(-2).join('.');
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${parentDomain}`;
      }
    });
    
    // Explicitly clear common auth cookie names
    const commonCookieNames = ['jwt', 'token', 'authToken', 'accessToken', 'refreshToken', 'session'];
    commonCookieNames.forEach(cookieName => {
      document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      const domain = window.location.hostname;
      const domainParts = domain.split('.');
      if (domainParts.length > 1) {
        const parentDomain = '.' + domainParts.slice(-2).join('.');
        document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${parentDomain}`;
      }
    });
  } catch (error) {
    console.error('Error clearing cookies:', error);
  }
};

export function useLogout() {
  const { logout } = useAuth();

  return () => {
    const savedTheme = rememberedTheme();
    // Clear local state immediately
    logout();
    
    // Clear all storage immediately, then put the theme back so login can use it.
    try {
      localStorage.clear();
      sessionStorage.clear();
      if (savedTheme) rememberTheme(savedTheme);
    } catch (error) {
      console.error('Error clearing storage:', error);
    }
    applyTheme(savedTheme ?? 'default');
    
    // Clear all cookies immediately
    clearAllCookies();

    // Use full URL for reliable redirect on mobile (relative paths can fail on some devices)
    const fullLoginUrl = window.location.origin + (loginUrl.startsWith('/') ? loginUrl : '/' + loginUrl);
    window.location.href = fullLoginUrl;

    // Call logout API in the background (fire and forget)
    // This ensures the server-side session is cleared, but doesn't block the redirect
    apiService.post(apiRoutes.auth.logout, {}, { withCredentials: true })
      .then(() => {
      })
      .catch((error) => {   
        console.error('Logout API call failed:', error);
        // Ignore error - user is already redirected
      });
  };
}